import { forwardRef, useCallback, useId, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

// Tabs — one panel visible at a time, selected by a keyboard-operable tablist
// (story 5-11b; P10 §2, D44).
//
// ── Why this is a primitive and not a one-off on the review screen ─────────
// P05 §10 asks the review surface for "labelled Inspection/Report tabs" on
// mobile, and CLAUDE.md's standing rule is that new UI composes a primitive or
// adds one HERE first — one-off components are a reject. There was no tab-like
// component anywhere in `src` at HEAD (checked: no Tabs, no accordion, nothing
// with `role="tablist"`), so this is a genuine addition rather than a
// promotion. Promotion candidates once a second and third consumer land: the
// report detail's version/content split, and the design gallery's own sections.
//
// ── CONTROLLED ONLY, deliberately ─────────────────────────────────────────
// No internal `useState` fallback. The review surface has to keep the selected
// tab in URL state (G-25: "filters / sort / page / cursor URL-serializable"),
// and a primitive that owned the value would either fight the URL or need a
// second "controlled mode" branch. One mode, so there is one source of truth.
//
// ── The a11y contract (WAI-ARIA Tabs pattern), decided once ───────────────
//   * `role="tablist"` wrapping `role="tab"` buttons, each owning its panel via
//     `aria-controls`, each panel pointing back with `aria-labelledby`.
//   * ROVING TABINDEX, not "every tab is tabbable": exactly one tab is in the
//     tab order (`tabIndex=0` on the selected one, `-1` on the rest), so Tab
//     moves INTO and OUT OF the tablist rather than through every tab. This is
//     the part hand-rolled tabs almost always get wrong, and it is why this is
//     a primitive.
//   * Arrow keys move selection and wrap; Home/End jump to the ends. The
//     roving-tabindex focus move is imperative (`.focus()`), because the
//     selected tab is the only one focus can land on and the DOM node has to be
//     told directly.
//   * ACTIVATION FOLLOWS FOCUS (the "automatic" variant of the pattern). Safe
//     here because both panels are already rendered client-side — there is no
//     fetch behind a tab, so arrowing through them cannot fire requests. A
//     surface with expensive panels would want manual activation, which is the
//     `activateOnFocus={false}` this deliberately does NOT ship until something
//     needs it (G-29: no aspirational surface).
//   * The panel is `tabIndex={0}` so a keyboard user can scroll it without a
//     focusable child, per the same pattern.

export interface TabItem {
  /** Stable identity. This is what `value`/`onChange` speak in, and what the
   * URL carries — never the index, which moves when a tab is added. */
  id: string;
  label: ReactNode;
  /** Optional count/status rendered after the label (e.g. an issue count). */
  badge?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  /** The selected tab's id. Controlled — see the header. */
  value: string;
  onChange: (next: string) => void;
  /** Names the tablist for screen readers. Required: an unlabelled tablist
   * announces as "tab list" and nothing else. */
  'aria-label': string;
  /** The panel body for the selected tab. Rendering is the caller's job — this
   * primitive owns selection and the a11y wiring, not content. */
  children: ReactNode;
  /** Stretch tabs to fill the row. The mobile default: two equal targets. */
  fill?: boolean;
  className?: string;
  panelClassName?: string;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  { items, value, onChange, children, fill = false, className = '', panelClassName = '', ...rest },
  ref,
) {
  // One stable id prefix per instance, so two Tabs on one page cannot collide
  // their `aria-controls`/`aria-labelledby` pairs.
  const uid = useId();
  const tabId = (id: string) => `${uid}-tab-${id}`;
  const panelId = (id: string) => `${uid}-panel-${id}`;
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const selectable = items.filter((item) => !item.disabled);

  const move = useCallback(
    (delta: number) => {
      if (selectable.length === 0) return;
      const current = selectable.findIndex((item) => item.id === value);
      // `+ length` before the modulo so a backwards step from index 0 wraps to
      // the end instead of producing -1.
      const next = selectable[(current + delta + selectable.length) % selectable.length];
      if (!next) return;
      onChange(next.id);
      refs.current[next.id]?.focus();
    },
    [onChange, selectable, value],
  );

  const jump = useCallback(
    (edge: 'first' | 'last') => {
      const next = edge === 'first' ? selectable[0] : selectable[selectable.length - 1];
      if (!next) return;
      onChange(next.id);
      refs.current[next.id]?.focus();
    },
    [onChange, selectable],
  );

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        move(1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        move(-1);
        break;
      case 'Home':
        event.preventDefault();
        jump('first');
        break;
      case 'End':
        event.preventDefault();
        jump('last');
        break;
      default:
        break;
    }
  };

  return (
    <div ref={ref} className={className} {...rest}>
      <div
        role="tablist"
        aria-label={rest['aria-label']}
        onKeyDown={onKeyDown}
        // `overflow-x-auto` — story 2-147c: found live at 390px that a tablist
        // with more tabs than fit the viewport had NO horizontal-scroll
        // fallback. `.glass-card`'s own `overflow: hidden` (tokens.css) then
        // silently CLIPS the extra tabs — worse than a page-level overflow,
        // since there is no scrollbar at all to discover them, not even by
        // touch swipe. `shrink-0` on each tab button (below) keeps every tab
        // at its natural width instead of being squeezed, so scrolling reveals
        // the real content rather than a row of truncated labels.
        className="flex gap-1 overflow-x-auto border-b border-border"
      >
        {items.map((item) => {
          const selected = item.id === value;
          return (
            <button
              key={item.id}
              // `type="button"` is mandatory — an untyped <button> inside a
              // <form> defaults to submit, the same trap `Switch` documents.
              type="button"
              role="tab"
              id={tabId(item.id)}
              aria-controls={panelId(item.id)}
              aria-selected={selected}
              disabled={item.disabled}
              // The roving tabindex — see the header.
              tabIndex={selected ? 0 : -1}
              ref={(node) => {
                refs.current[item.id] = node;
              }}
              onClick={() => onChange(item.id)}
              className={`focus-ring -mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 ${
                // `fill` stretches tabs to share the row equally (`flex-1`,
                // which already implies shrink) — never combined with a
                // narrow-viewport overflow case by design (a `fill` consumer
                // has few enough tabs to fit). Non-fill tabs get `shrink-0`
                // so the new `overflow-x-auto` tablist (above) scrolls
                // instead of squeezing labels.
                fill ? 'flex-1 justify-center' : 'shrink-0'
              } ${
                selected
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {item.label}
              {item.badge != null && item.badge !== false ? item.badge : null}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={panelId(value)}
        aria-labelledby={tabId(value)}
        // Focusable so a keyboard user can scroll a panel that contains no
        // focusable child — part of the same WAI-ARIA pattern.
        tabIndex={0}
        className={`focus-ring ${panelClassName}`}
      >
        {children}
      </div>
    </div>
  );
});
